import { useState } from 'react'
import { api, ApiRequestError } from '../api/client'
import type { ModeratorRow, UserRole } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { useAsync } from '../lib/useAsync'
import { number, percent, shortDate } from '../lib/format'
import { Avatar, ErrorState, PageHeader, Section, TableSkeleton } from '../components/ui'
import './Moderators.css'

/** Screen 7d · Admin · Moderatorlar. */

const ASSIGNABLE: { role: UserRole; label: string }[] = [
  { role: 'MODERATOR', label: 'Moderator' },
  { role: 'SENIOR_MODERATOR', label: 'Baş moderator' },
  { role: 'ADMIN', label: 'Admin' },
]

export function Moderators() {
  const { user, can } = useAuth()
  const { data, error, loading, reload } = useAsync(() => api.moderators(), [])
  const [busyId, setBusyId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  async function change(id: string, patch: Parameters<typeof api.updateModerator>[1]) {
    setBusyId(id)
    setActionError(null)
    try {
      await api.updateModerator(id, patch)
      reload()
    } catch (cause) {
      setActionError(
        cause instanceof ApiRequestError ? cause.message : 'Dəyişiklik yadda saxlanmadı.',
      )
    } finally {
      setBusyId(null)
    }
  }

  const active = data?.filter((m) => !m.suspended) ?? []
  const suspended = data?.filter((m) => m.suspended) ?? []

  return (
    <>
      <PageHeader
        title="Moderatorlar"
        subtitle={
          loading
            ? 'Yüklənir…'
            : `${number(active.length)} aktiv · ${number(suspended.length)} dayandırılmış · səlahiyyətləri buradan idarə et`
        }
      />

      {/* Səlahiyyət səviyyələri — the permission legend from the design. */}
      <section className="card levels">
        <h3>Səlahiyyət səviyyələri</h3>
        <p className="muted">
          <strong>Moderator:</strong> məzmun silmə + xəbərdarlıq ·{' '}
          <strong>Baş moderator:</strong> blok + etiraza baxma · <strong>Admin:</strong>{' '}
          moderator idarəsi
        </p>
      </section>

      {actionError && (
        <p className="moderators__error" role="alert">
          {actionError}
        </p>
      )}

      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : loading ? (
        <TableSkeleton rows={5} />
      ) : (
        <>
          <div className="table-scroll">
            <div className="table moderators__table">
              <div className="table__head">
                <span>Moderator</span>
                <span>Rol</span>
                <span>Qoşulma</span>
                <span>Qərar</span>
                <span>Dəqiqlik</span>
                <span>Səlahiyyət</span>
              </div>

              {active.map((row) => (
                <div key={row.id} className="table__row">
                  <span className="row moderators__person">
                    <Avatar initials={row.initials} />
                    <span className="moderators__names">
                      <span className="moderators__name">{row.displayName}</span>
                      <span className="muted moderators__handle">@{row.username}</span>
                    </span>
                  </span>

                  <span className="moderators__role">{row.roleLabel}</span>
                  <span className="muted">{shortDate(row.joinedAt)}</span>
                  <span className="numeral">{number(row.decisions)}</span>
                  <span
                    className={`numeral moderators__accuracy${
                      (row.accuracyPct ?? 100) < 75 ? ' moderators__accuracy--low' : ''
                    }`}
                  >
                    {percent(row.accuracyPct)}
                  </span>

                  <span>
                    {canManage(row, user?.id, can('ADMIN')) ? (
                      <div className="moderators__controls">
                        <select
                          className="select moderators__select"
                          value={row.role}
                          disabled={busyId === row.id}
                          onChange={(e) =>
                            void change(row.id, { role: e.target.value as UserRole })
                          }
                          aria-label={`${row.displayName} üçün rol`}
                        >
                          {ASSIGNABLE.map((option) => (
                            <option key={option.role} value={option.role}>
                              {option.label}
                            </option>
                          ))}
                          {/* Shown so a super admin's own row is not mislabelled. */}
                          {row.role === 'SUPER_ADMIN' && (
                            <option value="SUPER_ADMIN">Baş admin</option>
                          )}
                        </select>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={busyId === row.id}
                          onClick={() =>
                            void change(row.id, {
                              suspend: true,
                              suspensionReason: 'Admin qərarı',
                            })
                          }
                        >
                          Dayandır
                        </button>
                      </div>
                    ) : (
                      <span className="muted moderators__self">
                        {row.id === user?.id ? 'sən' : '—'}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {suspended.length > 0 && (
            <Section title="Dayandırılmış">
              <ul className="suspended">
                {suspended.map((row) => (
                  <li key={row.id} className="suspended__item">
                    <Avatar initials={row.initials} />
                    <div className="suspended__meta">
                      <div className="moderators__name">
                        {row.displayName} · <span className="muted">@{row.username}</span>
                      </div>
                      <div className="muted suspended__reason">
                        Dayandırılmış
                        {row.suspensionReason ? ` · ${row.suspensionReason}` : ''}
                        {row.wrongDecisions > 0 && (
                          <> · <span className="numeral">{row.wrongDecisions}</span> səhv qərar</>
                        )}
                      </div>
                    </div>
                    {can('ADMIN') && (
                      <div className="row">
                        <button
                          type="button"
                          className="btn btn--green btn--sm"
                          disabled={busyId === row.id}
                          onClick={() => void change(row.id, { suspend: false })}
                        >
                          Bərpa et
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={busyId === row.id}
                          onClick={() => {
                            if (
                              window.confirm(
                                `@${row.username} moderator siyahısından tam çıxarılsın?`,
                              )
                            ) {
                              void change(row.id, { remove: true })
                            }
                          }}
                        >
                          Tam çıxar
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </>
      )}
    </>
  )
}

/**
 * The server is the authority; this only decides whether to render controls
 * that would be refused anyway — nobody edits themselves, and only a super
 * admin edits a peer or above.
 */
function canManage(row: ModeratorRow, myId: string | undefined, isAdmin: boolean): boolean {
  if (!isAdmin) return false
  if (row.id === myId) return false
  return true
}
